import cx from "classnames"
import { useTranslation } from "react-i18next"
import { Modal, AddressBox, Button, Stack } from "@tutellus/tutellus-components"
import { useEthereum, useTransactions } from "../../../context"
import { Transaction } from "../../../domain/ethereum/Models/Transaction"
import { WalletState } from "../../../domain/ethereum/Models/WalletState"
import { CHAIN_CONFIG } from "../../../js/chain"
import { Eyebrow } from "../Eyebrow"
import { DataList } from "../DataList"
import { getTypeIcon } from "./utils"
import styles from "./index.module.css"

/* Patrón app-modal del DS 2026 (handoff docs/design-handoff-2026).
   El eyebrow va al principio del cuerpo porque Modal solo acepta title
   string (hueco nº8: prop eyebrow / slot de cabecera en la librería). */

interface WalletModalProps {
  onClose: () => void
}

export const WalletModal = ({ onClose }: WalletModalProps) => {
  const { t } = useTranslation("common")
  const { wallet, disconnectWallet } = useEthereum()
  const { transactions, cleanTransactions } = useTransactions()

  const hasTransactions = transactions.items.length > 0

  const dataItems = [
    {
      label: t("network"),
      value: (
        <span className={styles.chainValue}>
          <img className={styles.chainLogo} src={CHAIN_CONFIG.logo} alt="" />
          {CHAIN_CONFIG.name}
        </span>
      ),
    },
    {
      label: t("address"),
      value: <AddressBox account={wallet.account || ""} url={false} />,
    },
    {
      label: "Explorer",
      value: t("viewInExplorer"),
      href: wallet.explorerUrl,
    },
  ]

  const getStatusMeta = (status: string) => {
    if (status === Transaction.STATUS.SUCCESS) return { className: styles.success, label: t("transactions.status.SUCCESS") } // prettier-ignore
    if (status === Transaction.STATUS.REVERTED) return { className: styles.error, label: t("transactions.status.REVERTED") } // prettier-ignore
    return { className: styles.pending, label: t("transactions.status.PENDING") }
  }

  return (
    <Modal
      onClose={onClose}
      size="s"
      title={wallet.type === WalletState.TYPES.ABSTRACTED ? t("myHumanWallet") : t("yourAccount")}
      footer={
        <div className={styles.footer}>
          <Button type="outline" onClick={cleanTransactions} isDisabled={!hasTransactions} isFull>
            {t("clean")}
          </Button>
          <Button
            onClick={() => {
              disconnectWallet()
              onClose()
            }}
            isFull
          >
            {t("disconnect")}
          </Button>
        </div>
      }
    >
      <Stack gap="l">
        <Eyebrow>Wallet · {CHAIN_CONFIG.name}</Eyebrow>

        <DataList items={dataItems} />

        <Stack gap="s">
          <span className={styles.sectionLabel}>{t("transactions.title")}</span>
          {!hasTransactions ? (
            <p className={styles.noTransactions}>{t("transactions.noTransactions")}</p>
          ) : (
            <div className={styles.txList}>
              {transactions.items
                .sort((a, b) => b.date!.getTime() - a.date!.getTime())
                .map((transaction) => {
                  const status = getStatusMeta(transaction.status)
                  return (
                    <button
                      type="button"
                      className={styles.txRow}
                      key={transaction.hash || transaction.userOpHash}
                      onClick={() => window.open(transaction.explorerUrl, "_blank")}
                    >
                      <span className={styles.txIcon}>{getTypeIcon(transaction.type)}</span>
                      <span className={styles.txInfo}>
                        <span className={styles.txType}>{t(`transactions.types.${transaction.type}`)}</span>
                        <span className={styles.txDate}>{transaction.date?.toLocaleString()}</span>
                      </span>
                      <span className={cx(styles.txStatus, status.className)}>
                        <span className={styles.txStatusDot} aria-hidden="true" />
                        {status.label}
                      </span>
                    </button>
                  )
                })}
            </div>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
